import type { SeedIsland } from './seedLatin'

/**
 * The same two situations as the German expansion, in the Romance languages.
 *
 * The phone, because it is the one conversation with nothing to fall back on —
 * no face, no pointing, no writing it down. And officialdom, because anyone
 * actually living somewhere meets it early, the vocabulary is narrow and fixed,
 * and fumbling it costs another appointment rather than mild embarrassment.
 *
 * Deliberately the same two topics across all four languages: someone drilling
 * two of them should be practising comparable material, and the parallel makes
 * the shape of each language visible — where French needs a subjunctive, Spanish
 * reaches for a reflexive, Italian drops the pronoun entirely.
 */
export const EXTRA_ROMANCE: Record<string, SeedIsland[]> = {
  fr: [
    {
      slug: 'telephone',
      title: 'On the phone',
      nativeTitle: 'Au téléphone',
      useWhen:
        'No face to read and no pointing. The one conversation where a rehearsed opening earns its keep immediately.',
      tags: ['transactional', 'phone'],
      level: 3,
      blocks: [
        [
          "Bonjour, c'est Darin Molnar à l'appareil.",
          'Hello, this is Darin Molnar speaking.',
          "à l'appareil — literally \"at the device\" — is the fixed way to identify yourself by phone. Saying \"je suis\" here sounds like you are introducing yourself in person.",
        ],
        [
          'Pourriez-vous répéter, s’il vous plaît ? La ligne est mauvaise.',
          'Could you repeat that, please? The line is bad.',
          'Blaming the line asks for a repeat without announcing that your French failed. Pourriez-vous is the conditional — softer than pouvez-vous, and worth the extra syllable.',
        ],
        [
          "J'appelle pour prendre rendez-vous.",
          "I'm calling to make an appointment.",
          'prendre rendez-vous — take a meeting — with no article. A fixed expression; adding "un" is the giveaway.',
        ],
        [
          'Pourriez-vous parler un peu plus lentement ?',
          'Could you speak a little more slowly, please?',
          'Say it early, before you have lost the thread rather than after. It is worth more than any other line here.',
        ],
        [
          'Pourriez-vous me l’envoyer par e-mail ?',
          'Could you send it to me by email?',
          'Two pronouns stacked before the verb: me (to me) then le (it). French insists on that order — me le, never le me.',
        ],
        [
          'Je vous remercie de votre aide. Au revoir.',
          'Thank you for your help. Goodbye.',
          'Je vous remercie is a notch more formal than merci, which suits a call to someone you do not know.',
        ],
      ],
    },
    {
      slug: 'administration',
      title: 'Officialdom',
      nativeTitle: "À l'administration",
      useWhen:
        'Narrow vocabulary, fixed script, and a high price for getting it wrong — the definition of an island worth drilling.',
      tags: ['transactional', 'bureaucracy'],
      level: 3,
      blocks: [
        [
          "J'ai rendez-vous à dix heures au nom de Molnar.",
          "I have an appointment at ten o'clock under the name Molnar.",
          'au nom de — under the name of. The whole phrase is fixed; learn it as one lump rather than assembling it.',
        ],
        [
          'Quels documents devez-vous avoir ?',
          'Which documents do you need?',
          'Asking before you are asked is what saves the second appointment.',
        ],
        [
          "Je ne l'ai pas sur moi. Puis-je l'envoyer plus tard ?",
          "I don't have it on me. May I send it later?",
          'sur moi = on my person. Puis-je is the formal inversion of je peux — the register officialdom expects.',
        ],
        [
          "Pourriez-vous m'expliquer la suite ?",
          'Could you explain to me what happens next?',
          'la suite — what follows. Shorter and more idiomatic than asking "ce qui se passe après".',
        ],
        [
          "J'ai rempli le formulaire. Il manque quelque chose ?",
          'I have filled in the form. Is anything missing?',
          'Il manque is impersonal — literally "it lacks". French states absence this way rather than saying something *is* missing.',
        ],
        [
          "Je viens de m'installer dans le quartier.",
          "I've just moved into the neighbourhood.",
          'venir de + infinitive is the recent past: not "I come from", but "I have just". One of the highest-value patterns in French.',
        ],
      ],
    },
  ],

  es: [
    {
      slug: 'telefono',
      title: 'On the phone',
      nativeTitle: 'Por teléfono',
      useWhen:
        'Nothing to fall back on — no gestures, no lip reading. Rehearse the opening until it is automatic.',
      tags: ['transactional', 'phone'],
      level: 3,
      blocks: [
        [
          'Buenos días, le llamo de parte de Darin Molnar.',
          'Good morning, this is Darin Molnar calling.',
          'de parte de — on behalf of — is the standard telephone formula. le is the formal "you" as an object pronoun.',
        ],
        [
          '¿Podría repetirlo, por favor? Se oye mal.',
          'Could you repeat that, please? The line is bad.',
          'Se oye mal — "it is heard badly" — an impersonal se, which Spanish reaches for constantly. Blaming the line is the graceful way to ask twice.',
        ],
        [
          'Llamo para pedir una cita.',
          "I'm calling to make an appointment.",
          'pedir una cita — ask for an appointment. In Latin America you would more often hear "hacer una cita"; both are understood.',
        ],
        [
          '¿Podría hablar un poco más despacio?',
          'Could you speak a little more slowly?',
          'despacio, not lentamente — the everyday word. Say it early.',
        ],
        [
          '¿Me lo puede enviar por correo electrónico?',
          'Can you send it to me by email?',
          'Me lo — to me, it. Spanish stacks the indirect pronoun before the direct one, same order as French.',
        ],
        [
          'Muchas gracias por su ayuda. Hasta luego.',
          'Thank you very much for your help. Goodbye.',
          'su ayuda keeps the formal register you opened with. Switching to tu halfway through is the common slip.',
        ],
      ],
    },
    {
      slug: 'tramites',
      title: 'Officialdom',
      nativeTitle: 'Los trámites',
      useWhen:
        'Everyone who moves somewhere meets this early. Fixed vocabulary, and a real cost to fumbling it.',
      tags: ['transactional', 'bureaucracy'],
      level: 3,
      blocks: [
        [
          'Tengo cita a las diez a nombre de Molnar.',
          "I have an appointment at ten o'clock under the name Molnar.",
          'Tengo cita with no article — like "I have class". Adding "una" is not wrong but marks you as translating.',
        ],
        [
          'Quiero empadronarme. Acabo de mudarme.',
          "I'd like to register. I've just moved.",
          'empadronarse is the specific word for registering at the town hall. acabar de + infinitive is "to have just" — the same pattern as French venir de.',
        ],
        [
          '¿Qué documentos necesita de mí?',
          'Which documents do you need from me?',
          'necesita is the formal "you need". Asking first is what saves a second trip.',
        ],
        [
          'No lo llevo encima. ¿Puedo enviarlo después?',
          "I don't have it on me. Can I send it later?",
          'llevar encima — carry on top — is the idiom for having something on your person.',
        ],
        [
          '¿Me puede explicar cómo sigue el trámite?',
          'Could you explain to me how the process continues?',
          'el trámite is the whole bureaucratic procedure — a word with no clean English equivalent and no way around it here.',
        ],
        [
          'He rellenado el formulario. ¿Falta algo?',
          'I have filled in the form. Is anything missing?',
          'faltar works backwards from English: the thing missing is the subject. "¿Falta algo?" — does something lack?',
        ],
      ],
    },
  ],

  it: [
    {
      slug: 'telefono',
      title: 'On the phone',
      nativeTitle: 'Al telefono',
      useWhen:
        'The conversation with no gestures to lean on. Get the opening automatic and the rest follows.',
      tags: ['transactional', 'phone'],
      level: 3,
      blocks: [
        [
          'Buongiorno, sono Darin Molnar.',
          'Good morning, this is Darin Molnar.',
          'Italian simply uses sono — "I am". No equivalent of the French à l’appareil; the plainer form is the correct one.',
        ],
        [
          'Potrebbe ripetere, per favore? La linea è disturbata.',
          'Could you repeat that, please? The line is bad.',
          'disturbata — disturbed — is what Italians say of a bad line. Potrebbe is the formal conditional, softer than può.',
        ],
        [
          'Chiamo per fissare un appuntamento.',
          "I'm calling to make an appointment.",
          'fissare — to fix — is the verb that goes with appuntamento. prendere works too but fissare is more natural on the phone.',
        ],
        [
          'Potrebbe parlare un po’ più lentamente?',
          'Could you speak a little more slowly?',
          'un po’ is the clipped form of un poco, and the apostrophe is not optional in writing.',
        ],
        [
          'Me lo può mandare per email?',
          'Can you send it to me by email?',
          'Me lo — to me, it. Note the shift: mi becomes me before another pronoun. Italian does this automatically and it catches everyone.',
        ],
        [
          'La ringrazio molto. ArrivederLa.',
          'Thank you very much. Goodbye.',
          'ArrivederLa is the formal goodbye, capital L and all — arrivederci is the friendly one. On a call to a stranger, the formal is right.',
        ],
      ],
    },
    {
      slug: 'burocrazia',
      title: 'Officialdom',
      nativeTitle: 'In comune',
      useWhen:
        'Italian bureaucracy has its own vocabulary and no patience for improvisation. Drill it cold.',
      tags: ['transactional', 'bureaucracy'],
      level: 3,
      blocks: [
        [
          'Ho un appuntamento alle dieci a nome Molnar.',
          "I have an appointment at ten o'clock under the name Molnar.",
          'a nome — under the name — with no article. Fixed phrase.',
        ],
        [
          'Vorrei fare la residenza. Mi sono appena trasferito.',
          "I'd like to register as resident. I've just moved.",
          'fare la residenza is the exact bureaucratic phrase. appena + past tense is "just" — and trasferirsi is reflexive, so mi is required.',
        ],
        [
          'Quali documenti servono?',
          'Which documents are needed?',
          'servire works backwards from English, like Spanish faltar: the documents are the subject doing the serving.',
        ],
        [
          'Non ce l’ho con me. Posso portarlo dopo?',
          "I don't have it with me. Can I bring it later?",
          'Non ce l’ho — the ce is untranslatable filler that Italians would never omit. Learn the lump.',
        ],
        [
          'Mi può spiegare come si procede?',
          'Could you explain to me how one proceeds?',
          'si procede is the impersonal si — "one proceeds". Italian uses it where English says "what happens next".',
        ],
        [
          'Ho compilato il modulo. Manca qualcosa?',
          'I have filled in the form. Is anything missing?',
          'compilare, not riempire, for a form. Manca qualcosa mirrors the Spanish exactly — the missing thing is the subject.',
        ],
      ],
    },
  ],
}
